import type { ChatSource } from "@mirage/mosaics/Chat/types";
import i18n from "@mirage/translations";
import type { AccessType } from "./assist-api";
import type { ComposeAssistantConversationMessage } from "./assist-message";

export interface ComposeVoice {
  id: string;
  dataId: string;
  name: string;
  description: string;
  sources: ChatSource[];
  messagesHistory?: ComposeAssistantConversationMessage[];
  redraftedSamples?: ComposeVoiceSample[];
  originalSamples?: ComposeVoiceSample[];
  rev: number;
  accessType: AccessType;
  isOwned?: boolean;
}

export interface ComposeVoiceSample {
  contentType: ComposeVoicePreviewContentType;
  text: string;
}

export type DefaultComposeVoice = Pick<ComposeVoice, "name" | "description"> & {
  id: PreConfiguredVoiceID;
  dataId: PreConfiguredVoiceID;
};

export enum ComposeVoicePreviewContentType {
  CASE_STUDY = "Case Study",
  SALES_EMAIL = "Sales Email",
  COMPANY_NEWSLETTER = "Company Newsletter",
  ANALYSIS_REPORT = "Analysis Report",
  BLOG_POST = "Blog Post",
}

export const getVoiceSamplePreviewContentTypeLabel = (
  contentType: ComposeVoicePreviewContentType,
): string => {
  switch (contentType) {
    case ComposeVoicePreviewContentType.CASE_STUDY:
      return i18n.t("ai_tool_library_tone_sample_case_study");
    case ComposeVoicePreviewContentType.SALES_EMAIL:
      return i18n.t("ai_tool_library_tone_sample_sales_email");
    case ComposeVoicePreviewContentType.COMPANY_NEWSLETTER:
      return i18n.t("ai_tool_library_tone_sample_company_newsletter");
    case ComposeVoicePreviewContentType.ANALYSIS_REPORT:
      return i18n.t("ai_tool_library_tone_sample_analysis_report");
    case ComposeVoicePreviewContentType.BLOG_POST:
      return i18n.t("ai_tool_library_tone_sample_blog_post");
    default:
      throw new Error(`Unknown ComposeVoicePreviewContentType: ${contentType}`);
  }
};

// TODO (will) only using 3 samples until we can figure out a way to do 5 in a timely manner
// might be worth making an initial api call for the first and then lazy loading the rest
export const getComposeVoiceSamples = (): ComposeVoiceSample[] => [
  // {
  //   contentType: ComposeVoicePreviewContentType.CASE_STUDY,
  //   text: `ABC partnered with Zenfinity, a mid-sized logistics firm facing operational delays and customer dissatisfaction due to outdated systems. Over three months, ABC implemented its cutting-edge workflow automation software, reducing Zenfinity’s processing time by 35%. The transition was managed with minimal downtime, and within weeks, Zenfinity reported a 22% increase in customer satisfaction and a 15% boost in employee productivity. By enhancing Zenfinity's tracking and reporting systems, ABC helped streamline end-to-end logistics. This partnership demonstrates ABC’s commitment to empowering clients with scalable, impactful solutions that drive both efficiency and customer success.`,
  // },
  {
    contentType: ComposeVoicePreviewContentType.SALES_EMAIL,
    text: i18n.t("compose_voice_sample_sales_email"),
  },
  {
    contentType: ComposeVoicePreviewContentType.COMPANY_NEWSLETTER,
    text: i18n.t("compose_voice_sample_company_newsletter"),
  },
  {
    contentType: ComposeVoicePreviewContentType.ANALYSIS_REPORT,
    text: i18n.t("compose_voice_sample_analysis_report"),
  },
  // {
  //   contentType: ComposeVoicePreviewContentType.BLOG_POST,
  //   text: `Title: How Automation is Transforming the Future of Work\n\nThe automation landscape is evolving rapidly, and businesses are feeling the impact in ways both big and small. At ABC, we’ve seen firsthand how automation can unlock efficiencies, enhance customer experiences, and ultimately drive growth. For instance, automation in data processing alone has reduced routine administrative tasks by as much as 40%, freeing teams to focus on strategic initiatives.\n\nBut this transformation goes beyond just efficiency; it’s about enabling organizations to rethink the way they operate. Companies that embrace automation are poised to lead their industries, achieving faster response times, higher customer satisfaction, and increased profitability. The future is here, and ABC is excited to help our clients harness these possibilities to build a more innovative tomorrow.`,
  // },
];

export const getPreConfiguredVoiceIdRedraftedSamples = () => ({
  preset_neutral: [
    {
      contentType: ComposeVoicePreviewContentType.SALES_EMAIL,
      text: i18n.t("compose_voice_sample_sales_email"),
    },
    {
      contentType: ComposeVoicePreviewContentType.COMPANY_NEWSLETTER,
      text: i18n.t("compose_voice_sample_company_newsletter"),
    },
    {
      contentType: ComposeVoicePreviewContentType.ANALYSIS_REPORT,
      text: i18n.t("compose_voice_sample_analysis_report"),
    },
  ],
  preset_persuasive: [
    {
      contentType: ComposeVoicePreviewContentType.SALES_EMAIL,
      text: i18n.t("compose_voice_sample_sales_email_informative"),
    },
    {
      contentType: ComposeVoicePreviewContentType.COMPANY_NEWSLETTER,
      text: i18n.t("compose_voice_sample_company_newsletter_informative"),
    },
    {
      contentType: ComposeVoicePreviewContentType.ANALYSIS_REPORT,
      text: i18n.t("compose_voice_sample_analysis_report_informative"),
    },
  ],
  preset_instructional: [
    {
      contentType: ComposeVoicePreviewContentType.SALES_EMAIL,
      text: i18n.t("compose_voice_sample_sales_email_instructional"),
    },
    {
      contentType: ComposeVoicePreviewContentType.COMPANY_NEWSLETTER,
      text: i18n.t("compose_voice_sample_company_newsletter_instructional"),
    },
    {
      contentType: ComposeVoicePreviewContentType.ANALYSIS_REPORT,
      text: i18n.t("compose_voice_sample_analysis_report_instructional"),
    },
  ],
  preset_narrative: [
    {
      contentType: ComposeVoicePreviewContentType.SALES_EMAIL,
      text: i18n.t("compose_voice_sample_sales_email_narrative"),
    },
    {
      contentType: ComposeVoicePreviewContentType.COMPANY_NEWSLETTER,
      text: i18n.t("compose_voice_sample_company_newsletter_narrative"),
    },
    {
      contentType: ComposeVoicePreviewContentType.ANALYSIS_REPORT,
      text: i18n.t("compose_voice_sample_analysis_report_narrative"),
    },
  ],
  preset_informal: [
    {
      contentType: ComposeVoicePreviewContentType.SALES_EMAIL,
      text: i18n.t("compose_voice_sample_sales_email_informal"),
    },
    {
      contentType: ComposeVoicePreviewContentType.COMPANY_NEWSLETTER,
      text: i18n.t("compose_voice_sample_company_newsletter_informal"),
    },
    {
      contentType: ComposeVoicePreviewContentType.ANALYSIS_REPORT,
      text: i18n.t("compose_voice_sample_analysis_report_narrative"),
    },
  ],
  preset_analytical: [
    {
      contentType: ComposeVoicePreviewContentType.SALES_EMAIL,
      text: i18n.t("compose_voice_sample_sales_email_analytical"),
    },
    {
      contentType: ComposeVoicePreviewContentType.COMPANY_NEWSLETTER,
      text: i18n.t("compose_voice_sample_company_newsletter_analytical"),
    },
    {
      contentType: ComposeVoicePreviewContentType.ANALYSIS_REPORT,
      text: i18n.t("compose_voice_sample_analysis_report_analytical"),
    },
  ],
});
export const PRE_CONFIGURED_VOICE_IDS = [
  "preset_neutral",
  "preset_persuasive",
  "preset_instructional",
  "preset_narrative",
  "preset_informal",
  "preset_analytical",
] as const;
export type PreConfiguredVoiceID = (typeof PRE_CONFIGURED_VOICE_IDS)[number];

export const getPreConfiguredVoiceIdToDraftName = () => ({
  preset_neutral: i18n.t("compose_preset_neutral_tones_label_title_draft"),
  preset_persuasive: i18n.t(
    "compose_preset_persuasive_tones_label_title_draft",
  ),
  preset_instructional: i18n.t(
    "compose_preset_instructional_tones_label_title_draft",
  ),
  preset_narrative: i18n.t("compose_preset_narrative_tones_label_title_draft"),
  preset_informal: i18n.t("compose_preset_informal_tones_label_title_draft"),
  preset_analytical: i18n.t(
    "compose_preset_analytical_tones_label_title_draft",
  ),
});
export const DEFAULT_PRECONFIGURED_VOICE_ID: PreConfiguredVoiceID =
  PRE_CONFIGURED_VOICE_IDS[0];

export function isPreConfiguredVoiceID(
  voiceID: string,
): voiceID is PreConfiguredVoiceID {
  return PRE_CONFIGURED_VOICE_IDS.includes(voiceID as PreConfiguredVoiceID);
}

export type InputContext = {
  type: "selection";
  selectedText: string;
};

export function createDefaultTones(): DefaultComposeVoice[] {
  return PRE_CONFIGURED_VOICE_IDS.map((id) => ({
    id,
    name: getPreConfiguredVoiceIdToDraftName()[id],
    dataId: id,
    description: "",
  }));
}

export function enhanceVoiceWithSamples(voice: ComposeVoice): ComposeVoice {
  // If it's a pre-configured voice and doesn't already have samples, add them
  if (isPreConfiguredVoiceID(voice.id) && !voice.redraftedSamples?.length) {
    return {
      ...voice,
      originalSamples: getComposeVoiceSamples(),
      redraftedSamples:
        getPreConfiguredVoiceIdRedraftedSamples()[
          voice.id as PreConfiguredVoiceID
        ] || [],
    };
  }
  // Return unchanged if not pre-configured or already has samples
  return voice;
}
